import { Box, styled, Tooltip } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface FadingTextBoxProps {
  label: string;
  width?: number;
}

const Container = styled(Box, {
  shouldForwardProp: prop => prop !== 'width',
})<{ width: number }>(({ theme: { utils, breakpoints }, width }) => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: utils.clamp(10, 20),
  fontWeight: 500,
  height: utils.clamp(20, 38),
  padding: `0 5px`,
  maxWidth: width,
  overflow: 'hidden',

  [breakpoints.down('xl')]: {
    padding: `0 2px`,
  },
}));

const CityBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'fade',
})<{ fade: boolean }>(({ fade }) => ({
  flexShrink: 1,
  flexGrow: 1,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'clip',
  display: 'inline-block',
  ...(fade && {
    maskImage: 'linear-gradient(to right, black 75%, transparent)',
    WebkitMaskImage: 'linear-gradient(to right, black 75%, transparent)',
  }),
}));

const StateBox = styled(Box)(({ theme: { spacing } }) => ({
  flexShrink: 0,
  marginLeft: spacing(0.5),
  whiteSpace: 'nowrap',
}));

export const FadingTextBox = ({ label, width = 160 }: FadingTextBoxProps) => {
  const parts = label.split(',').map(p => p.trim());
  const cityRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldFade, setShouldFade] = useState(false);

  useEffect(() => {
    if (cityRef.current && containerRef.current) {
      const fadeNeeded =
        cityRef.current.scrollWidth > cityRef.current.clientWidth;
      setShouldFade(fadeNeeded);
    }
  }, [label, width]);

  if (parts.length === 2) {
    const [city, state] = parts;

    return (
      <Tooltip title={label} arrow>
        <Container width={width} ref={containerRef}>
          <CityBox ref={cityRef} fade={shouldFade}>
            {`${city}, `}
          </CityBox>
          <StateBox>{state}</StateBox>
        </Container>
      </Tooltip>
    );
  }

  return <Container width={width}>{label}</Container>;
};
